import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useFlag } from '@simplywallst/unleash';

import { queryFn } from './portfolioQueryFn';
import { QUERY_KEY as PORTFOLIOS_QUERY_KEY } from './usePortfolios';

export const QUERY_KEY = 'PORTFOLIO' as const;

const THIRTY_SECONDS = 30 * 1000;
const PROCESSING_POLL_INTERVAL = 5000;

export function usePortfolio({ id }: { id: string }) {
  const flag = useFlag('mono-tx-portfolio-performance-optimization');
  const willUseOptimizedPerformance = flag.ready && flag.enabled;

  const queryClient = useQueryClient();

  const { data, status, isFetched, isFetching, error } = useQuery({
    enabled: id.length > 0,
    queryKey: [QUERY_KEY, id],
    queryFn: async () => queryFn(id),
    retry: 1,
    staleTime: 3600000,
    refetchInterval(query) {
      const { data } = query.state;
      const analysisStatus = data?.analysisStatus;
      const linkStatus = data?.link?.status;
      /**
       * If the portfolio is currently processing its analysis, refresh the query every 30s.
       * The length taken to Analyse a portfolio can increase depending on its size.
       * Ignore this condition if we are testing.
       */
      if (
        willUseOptimizedPerformance &&
        typeof window !== 'undefined' &&
        !window?.TEST_RUNNER &&
        analysisStatus === 'processing'
      ) {
        return PROCESSING_POLL_INTERVAL;
      }
      /**
       * If the portfolio is currently importing or resyncing from a brokerage, refresh
       * the query every 30s. Importing and resyncing is expected to take a few minutes.
       * Ignore this condition if we are testing.
       */
      if (
        typeof window !== 'undefined' &&
        !window?.TEST_RUNNER &&
        linkStatus !== undefined &&
        ['importing', 'resyncing'].includes(linkStatus)
      ) {
        return THIRTY_SECONDS;
      }

      return Infinity;
    },
    meta: {
      tags: ['narrative', 'portfolio'],
    },
  });

  const linkStatus = data?.link?.status;

  useEffect(() => {
    if (status === 'success' && typeof linkStatus !== 'undefined') {
      /**
       * If a linked portfolio's status has updated, we assume its data
       * has appreciably changed too (Example: `imported` -> `synced`).
       * We then refresh the portfolios query so it is also up to date.
       */
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_QUERY_KEY });
    }
  }, [linkStatus, queryClient, status]);

  return {
    data,
    error,
    status,
    isFetched,
    isFetching,
  };
}
